import {
  type Box3,
  type Mesh,
  type Object3D,
  Plane,
  type Ray,
  type Raycaster,
  Vector3,
} from "three";

/**
 * Pointer and wheel policy of the free-orbit camera: which surface point a
 * press or a wheel notch turns the camera around, and when.
 *
 * Kept free of React and of the camera-controls instance it drives, so every
 * rule can be tested on its own. `ProductViewerCamera` wires it to the DOM.
 */

/**
 * `userData` flags of scene objects that are not product surface: drag
 * arrows, guide lines and snap markers, the measurement overlay, and the
 * instanced draw that stands in for batched hardware (its logical meshes stay
 * in the part tree and are hit there instead).
 */
const NON_SURFACE_USER_DATA_FLAGS = [
  "isDragHelper",
  "isMeasurementBox",
  "isHardwareBatch",
] as const;

const isNonSurfaceObject = (object: Object3D): boolean =>
  !object.visible ||
  NON_SURFACE_USER_DATA_FLAGS.some((flag) => Boolean(object.userData?.[flag]));

/**
 * Whether `object` and every ancestor up to `root` count as visible product
 * surface.
 *
 * Deliberately ignores `material.visible`: a mesh whose draw moved into a
 * hardware batch keeps an invisible proxy material, yet it is the part the
 * user sees and points at.
 */
const isProductSurface = (object: Object3D, root: Object3D): boolean => {
  let current: Object3D | null = object;
  while (current) {
    if (isNonSurfaceObject(current)) return false;
    if (current === root) return true;
    current = current.parent;
  }
  return true;
};

const isMesh = (object: Object3D): object is Mesh =>
  (object as Partial<Mesh>).isMesh === true;

/**
 * The nearest visible product surface point along the raycaster's ray, or
 * `null` when the ray misses the product.
 *
 * Uses three's recursive intersection, so the `raycast` overrides of the part
 * tree still apply (a hidden part type opts its subtree out of hit testing).
 * Lines and points are skipped: their hit threshold is in world units and
 * would catch an edge outline from far beside it.
 */
export const pickProductSurfacePoint = (
  root: Object3D,
  raycaster: Raycaster,
): Vector3 | null => {
  for (const hit of raycaster.intersectObject(root, true)) {
    if (isMesh(hit.object) && isProductSurface(hit.object, root)) {
      return hit.point.clone();
    }
  }
  return null;
};

/** What a press needs to tell about itself and the viewer. */
export interface PivotPressInput {
  /** `PointerEvent.button`: 0 is the main button, and every touch or pen. */
  button: number;
  isPrimary: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
  /** Pointers other than this one that are already pressed on the viewer. */
  otherActivePointers: number;
  freeOrbit: boolean;
  controlsEnabled: boolean;
}

/**
 * Whether a press moves the orbit point onto the surface under it.
 *
 * Only the free-orbit viewer has a movable pivot. Only the first pointer of a
 * gesture sets it: a second finger starts a pinch, not a new orbit. Right and
 * middle presses pan and dolly around the pivot they find. A press with
 * ⌘/Ctrl drags a part and keeps the pivot, so the view does not change under
 * the drag.
 */
export const shouldPressSetPivot = (press: PivotPressInput): boolean =>
  press.freeOrbit &&
  press.controlsEnabled &&
  press.isPrimary &&
  press.otherActivePointers === 0 &&
  press.button === 0 &&
  !press.metaKey &&
  !press.ctrlKey;

/**
 * Records a press in `activePointers` (pointer id → pointer type) and returns
 * how many other pointers are still down.
 *
 * A pointerup can get lost (released outside the window, a touch screen that
 * sends neither pointerup nor pointercancel), and an entry left behind would
 * refuse every later press. A primary press is proof against that: no other
 * pointer of its type is down, and a primary mouse press means no gesture is
 * in progress at all, so it drops every entry. A primary touch or pen press
 * keeps a mouse button that is really held.
 */
export const registerPointerDown = (
  activePointers: Map<number, string>,
  press: { pointerId: number; pointerType: string; isPrimary: boolean },
): number => {
  if (press.isPrimary) {
    for (const [id, type] of activePointers) {
      if (press.pointerType === "mouse" || type === press.pointerType) {
        activePointers.delete(id);
      }
    }
  }
  const otherActivePointers = activePointers.size;
  activePointers.set(press.pointerId, press.pointerType);
  return otherActivePointers;
};

const _plane = new Plane();
const _planeNormal = new Vector3();

/**
 * The point a wheel notch should dolly toward.
 *
 * - On a hit: the hit.
 * - On a miss: where the cursor ray meets the plane through the product's
 *   bounding-box centre that faces the camera, so zooming beside the product
 *   approaches it at its own depth.
 * - Without product bounds, or when that plane is not in front of the camera:
 *   `null`, the current orbit point stays.
 */
export const resolveDollyPivot = ({
  hit,
  ray,
  productBounds,
  cameraDirection,
}: {
  hit: Vector3 | null;
  ray: Ray;
  productBounds: Box3 | null;
  /** The camera's world-space viewing direction (unit length). */
  cameraDirection: Vector3;
}): Vector3 | null => {
  if (hit) return hit.clone();
  if (!productBounds || productBounds.isEmpty()) return null;
  _planeNormal.copy(cameraDirection).normalize();
  _plane.setFromNormalAndCoplanarPoint(
    _planeNormal,
    productBounds.getCenter(new Vector3()),
  );
  return ray.intersectPlane(_plane, new Vector3());
};

/**
 * Distance the camera may come to its orbit point, in metres. Close enough to
 * inspect a screw head, far enough from the 1 cm near plane that the surface
 * is never clipped.
 */
export const MIN_ORBIT_DISTANCE = 0.02;

/** Factor the zoom buttons scale the orbit distance by per press. */
export const ZOOM_BUTTON_FACTOR = 0.8;

/**
 * The orbit distance after one zoom button press: a constant ratio, so a step
 * is as large near the surface as it looks from afar, and the camera never
 * jumps onto the orbit point.
 */
export const zoomButtonDistance = (
  distance: number,
  direction: "in" | "out",
  { minDistance, maxDistance }: { minDistance: number; maxDistance: number },
): number => {
  const next =
    direction === "in"
      ? distance * ZOOM_BUTTON_FACTOR
      : distance / ZOOM_BUTTON_FACTOR;
  return Math.min(maxDistance, Math.max(minDistance, next));
};

/** Pause between wheel events, in ms, after which a notch picks anew. */
export const WHEEL_REPICK_IDLE_MS = 150;

/** Depth by which a fresh hit must lie in front of the pivot to replace it. */
const wheelPivotDepthTolerance = (pivotDepth: number) =>
  Math.max(0.001, pivotDepth * 0.01);

export interface WheelPickState {
  clientX: number;
  clientY: number;
  timeStamp: number;
  /**
   * Depth in front of the camera, in metres: of the burst's orbit point for
   * the last pick (kept current as the camera moves), of the fresh hit for a
   * new event. `null` when there is none.
   */
  depth: number | null;
}

/**
 * Whether a wheel event moves the orbit point to the surface under the
 * cursor again, or keeps dollying toward the point its burst picked. Within a
 * burst the camera travels along the cursor ray, so that point stays under
 * the cursor, and setting it again would only freeze the running, smoothed
 * dolly. Any cursor travel sets it anew: a pixel can be all it takes to move
 * from a panel onto a handle in front of it. So does a surface that has come
 * in front of the orbit point with the cursor at rest (a lid swinging in, a
 * rebuild): the next notch would otherwise carry the camera through it.
 */
export const shouldRepickWheelPivot = (
  last: WheelPickState | null,
  next: WheelPickState,
): boolean => {
  if (!last) return true;
  if (next.timeStamp - last.timeStamp > WHEEL_REPICK_IDLE_MS) return true;
  if (next.clientX !== last.clientX || next.clientY !== last.clientY) {
    return true;
  }
  return (
    next.depth !== null &&
    last.depth !== null &&
    next.depth < last.depth - wheelPivotDepthTolerance(last.depth)
  );
};

/** The part of the camera-controls API the pivot setter needs. */
export interface OrbitPointControls {
  readonly active: boolean;
  readonly camera: {
    position: Vector3;
    getWorldDirection(out: Vector3): Vector3;
  };
  readonly distance: number;
  minDistance: number;
  getTarget(out: Vector3, receiveEndValue?: boolean): Vector3;
  getPosition(out: Vector3, receiveEndValue?: boolean): Vector3;
  getFocalOffset(out: Vector3, receiveEndValue?: boolean): Vector3;
  setLookAt(
    positionX: number,
    positionY: number,
    positionZ: number,
    targetX: number,
    targetY: number,
    targetZ: number,
    enableTransition?: boolean,
  ): unknown;
  setFocalOffset(
    x: number,
    y: number,
    z: number,
    enableTransition?: boolean,
  ): unknown;
  setOrbitPoint(x: number, y: number, z: number): void;
  dollyTo(distance: number, enableTransition?: boolean): unknown;
  update(delta: number): boolean;
}

const _target = new Vector3();
const _position = new Vector3();
const _focalOffset = new Vector3();
const _viewDirection = new Vector3();

/**
 * Ends a running transition where the camera is now.
 *
 * camera-controls computes a new orbit point from the camera's current pose
 * but writes it into the end values, so calling `setOrbitPoint` mid-transition
 * would bake a pose the camera never reached. `stop()` is no way out either:
 * it jumps to the end of the transition. Setting the end values to the
 * current ones settles the camera on the spot.
 */
export const freezeControlsAtCurrentPose = (
  controls: OrbitPointControls,
): void => {
  if (!controls.active) return;
  controls.getTarget(_target, false);
  controls.getPosition(_position, false);
  controls.getFocalOffset(_focalOffset, false);
  controls.setLookAt(
    _position.x,
    _position.y,
    _position.z,
    _target.x,
    _target.y,
    _target.z,
    false,
  );
  controls.setFocalOffset(
    _focalOffset.x,
    _focalOffset.y,
    _focalOffset.z,
    false,
  );
};

/**
 * Makes `point` the orbit point without moving the camera. Returns `false`
 * and leaves the pivot alone when the point is behind the camera or closer
 * than the minimum distance, where camera-controls would clamp the distance
 * and jump.
 *
 * camera-controls keeps the camera where it is by splitting the offset to the
 * point into an orbit radius (the straight distance) and a focal offset whose
 * depth part pulls the camera back toward the point. A dolly then scales only
 * the radius, so the camera would cross the point's plane before the radius
 * reaches zero. Folding the depth part into the radius makes the radius the
 * depth of the point in front of the camera: every dolly step is then a fixed
 * share of the way to the surface, and the camera cannot reach it.
 */
/** How far `point` lies in front of the camera, along its view direction. */
export const depthInFront = (
  camera: OrbitPointControls["camera"],
  point: Vector3,
): number => {
  camera.getWorldDirection(_viewDirection);
  return _position.copy(point).sub(camera.position).dot(_viewDirection);
};

export const setOrbitPointAtSurface = (
  controls: OrbitPointControls,
  point: Vector3,
): boolean => {
  if (depthInFront(controls.camera, point) < controls.minDistance) {
    return false;
  }

  // `update(0)` below wakes an idle camera without a later rest event, so
  // `active` can stay true and this freeze then runs as a harmless no-op.
  freezeControlsAtCurrentPose(controls);
  controls.setOrbitPoint(point.x, point.y, point.z);
  controls.getFocalOffset(_focalOffset);
  const radiusAtDepth = controls.distance + _focalOffset.z;
  if (radiusAtDepth >= controls.minDistance) {
    controls.dollyTo(radiusAtDepth, false);
    controls.setFocalOffset(_focalOffset.x, _focalOffset.y, 0, false);
  }
  // Applies the new orbit at once. camera-controls measures a dolly-to-cursor
  // step against the radius of its last update, so a wheel notch handled
  // before the next frame would otherwise read the jump from the old radius
  // to the new one as dolly travel and throw the camera sideways.
  controls.update(0);
  return true;
};
